import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { toDate, format } from 'date-fns'
import { CollapsibleSubHeading } from 'components/headings'
import Clickable from 'components/clickable'
import classNames from 'classnames'
import { map } from 'lodash'
import { Collapse } from 'react-bootstrap'
import Link from 'components/link'
import { numberToCurrency } from 'lib/money'

const CreditHistoryTabContent = ({ activeCredits, lineItems }) => {
  const [showAvailable, setShowAvailable] = useState(true)
  const [showHistory, setShowHistory] = useState(true)

  const creditDisplayText = (credit) => {
    if (credit.isPromotional) {
      let text = ` ${credit.campaignName} Promo`
      return text
    }
    if (credit.isReturn) {
      return 'Return Credit'
    }
    if (credit.isGroupGiftContribution) {
      return `Group Gift Contribution`
    }
    return `Gift Card (${credit.giftCardCode})`
  }

  const creditExpirationText = (credit) => {
    const { expiresAt } = credit
    if (typeof expiresAt === 'string') {
      const date = toDate(new Date(expiresAt))
      return `Expires ${format(date, 'M/dd/yyyy')}`
    }
    return 'Does not expire'
  }

  const MemoizedActiveCredits = useMemo(() => {
    return (
      <div>
        <ul className="list-unstyled pan man">
          {map(activeCredits, (credit) => {
            return (
              <li key={credit.id} className="mbm">
                <div className="flex align-center">
                  <div className="flex col grow">
                    <span className="small text-credit text-bold">
                      {numberToCurrency(credit.balance)}
                    </span>
                    <div className="h6 man text-bold">
                      {creditDisplayText(credit)}
                    </div>
                  </div>
                  <em className="small">{creditExpirationText(credit)}</em>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
    )
  }, [activeCredits])

  const MemoizedLineItems = useMemo(() => {
    return (
      <ul className="list-unstyled pan man">
        {map(lineItems, (lineItem) => {
          return (
            <li key={lineItem.id} className="mbm">
              <strong
                className={classNames(
                  lineItem.amount < 0 ? 'text-deduction' : 'text-credit',
                  'small text-bold'
                )}
              >
                {numberToCurrency(lineItem.amount)}
              </strong>
              <span className="text-bold small"> {lineItem.displayType}</span>
              <div className="man small">
                {lineItem.detailsUrl ? (
                  <Link url={lineItem.detailsUrl}>{lineItem.description}</Link>
                ) : (
                  lineItem.description
                )}
              </div>
            </li>
          )
        })}
      </ul>
    )
  }, [lineItems])

  return (
    <>
      {activeCredits.length > 0 && (
        <>
          <Clickable
            aria-label={`${
              showAvailable ? 'Collapse' : 'Expand'
            } Available Credits Section`}
            onClick={() => setShowAvailable(!showAvailable)}
            style={{ width: '100%' }}
          >
            <CollapsibleSubHeading collapsed={!showAvailable}>
              Available
            </CollapsibleSubHeading>
          </Clickable>
          <Collapse in={showAvailable}>{MemoizedActiveCredits}</Collapse>
        </>
      )}

      {lineItems.length > 0 ? (
        <div className={showAvailable && 'mtxxl'}>
          <Clickable
            aria-label={`${
              showHistory ? 'Collapse' : 'Expand'
            } Credit History Section`}
            onClick={() => setShowHistory(!showHistory)}
            style={{ width: '100%' }}
          >
            <CollapsibleSubHeading collapsed={!showHistory}>
              History
            </CollapsibleSubHeading>
          </Clickable>
          <Collapse in={showHistory}>{MemoizedLineItems}</Collapse>
        </div>
      ) : (
        <div className="mtl h6 text-center">
          <em>No credit history available.</em>
        </div>
      )}
    </>
  )
}

CreditHistoryTabContent.propTypes = {
  activeCredits: PropTypes.arrayOf(
    PropTypes.shape({
      balance: PropTypes.number.isRequired,
      campaignName: PropTypes.string,
      expiresAt: PropTypes.string,
      giftCardCode: PropTypes.string,
      isPromotional: PropTypes.bool.isRequired,
      isReturn: PropTypes.bool.isRequired,
    })
  ),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      createdAt: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      detailsUrl: PropTypes.string,
      displayType: PropTypes.string,
    })
  ),
}

CreditHistoryTabContent.defaultProps = {
  activeCredits: [],
  lineItems: [],
}

export default CreditHistoryTabContent
